<template>
    <div class="row mx-auto mt-2 px-0 col-12">
        <page-title :title="$t('registers.electronicLecturers.title')" :showTitle="false"></page-title>
        <div class="card">
            <div class="card-header bg-primary text-white">
                <div class="card-title h5 m-0">
                    {{ $t('registers.electronicLecturers.title') }}
                </div>
            </div>

            <div class="card-header" v-if="hasReadRights">
                <i class="fas fa-cogs pointer fa-2x float-right text-success"
                   @click.stop="changeGridSettings"
                   data-toggle="tooltip"
                   :title="settingsTooltipText"></i>

                <grid-user-settings ref="userSettings"
                                    :schemaModelFields="schemaModelFields"
                                    :componentName="componentName"
                                    :useLabelAsTitle="true"
                                    :showUserSettings="true"
                                    :gridOptions="userSettings"
                                    v-if="showUserSettingsForm"
                                    @saveGridSettings="saveGridSettings"
                                    @loadGridSettings="loadGridSettings"
                                    @deleteGridSettings="deleteGridSettings"
                                    @userSettingsChanged="userSettingsChanged">
                </grid-user-settings>

            </div>
            <div class="card-body">
                <div class="btn-toolbar justify-content-between mt-0 mb-2" role="toolbar">
                    <div class="justify-content-center col-2">
                        <button type="button" class="btn btn-secondary" style="width: 80%" @click="excelExport">
                            {{$t('buttons.excelExport.tooltipText')}}
                        </button>
                    </div>

                    <div class="input-group col-6">
                        <input v-model="searchQuery" type="text" v-on:keyup.enter.stop="massSearch"
                               v-on:keyup.esc.stop="clearFilters" class="form-control" aria-label="Mass search input"
                               aria-describedby="massSearch">
                        <button class="btn btn-secondary" @click.stop="massSearch">
                            <i class="fas fa-search"></i>
                            {{ $t('buttons.search.btnText') }}
                        </button>
                        <button class="btn btn-secondary"
                                @click.stop="clearFilters">
                            <i class="fas fa-times"></i>
                            {{ $t('buttons.clearFilters.btnText') }}
                        </button>
                    </div>
                </div>

                <kendo-datasource ref="datasource1"
                                  :transport="transportSettings"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="schemaModelFields"
                                  :schema-total="'total'"
                                  :schema-data="'data'"
                                  :page-size='defaultGridRows'
                                  :server-paging="true"
                                  :server-sorting="true"
                                  :server-filtering="true"
                                  :error="dataSourceError">
                </kendo-datasource>

                <kendo-grid ref="grid"
                            :data-source-ref="'datasource1'"
                            :editable="gridOptions.editable"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable"
                            :dataBound="dataBound">
                    <kendo-grid-column :field="'id'"
                                       :filterable="false"
                                       :width="100"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="true"
                                       :title="$t('registers.electronicLecturers.gridHeaders.id')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'fullName'"
                                       :width="300"
                                       :lockable="true"
                                       :locked="false"
                                       :title="$t('registers.electronicLecturers.gridHeaders.fullName')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'firstName'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :title="$t('registers.electronicLecturers.gridHeaders.firstName')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'middleName'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :title="$t('registers.electronicLecturers.gridHeaders.middleName')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'lastName'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :title="$t('registers.electronicLecturers.gridHeaders.lastName')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'positionStatistic'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :title="$t('registers.electronicLecturers.gridHeaders.positionStatistic')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'position'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :title="$t('registers.electronicLecturers.gridHeaders.position')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'institutionCompanyName'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :template="(e) => gridTextCellTemplate(e, 'institutionCompanyName')"
                                       :title="$t('registers.electronicLecturers.gridHeaders.institutionCompanyName')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'institutionCompanyCountry'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :title="$t('registers.electronicLecturers.gridHeaders.institutionCompanyCountry')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'institutionCompanyCity'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :title="$t('registers.electronicLecturers.gridHeaders.institutionCompanyCity')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'thematicAreas'"
                                       :width="250"
                                       :lockable="true"
                                       :locked="false"
                                       :template="(e) => gridTextCellTemplate(e, 'thematicAreas')"
                                       :title="$t('registers.electronicLecturers.gridHeaders.thematicAreas')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'themes'"
                                       :width="450"
                                       :lockable="true"
                                       :locked="false"
                                       :template="(e) => gridTextCellTemplate(e, 'themes')"
                                       :title="$t('registers.electronicLecturers.gridHeaders.themes')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'lectorsTeaching'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :template="(e) => gridBoolCellTemplate(e, 'lectorsTeaching')"
                                       :title="$t('registers.electronicLecturers.gridHeaders.lectorsTeaching')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'universityTeaching'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :template="(e) => gridBoolCellTemplate(e, 'universityTeaching')"
                                       :title="$t('registers.electronicLecturers.gridHeaders.universityTeaching')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'nipTeaching'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :template="(e) => gridBoolCellTemplate(e, 'nipTeaching')"
                                       :title="$t('registers.electronicLecturers.gridHeaders.nipTeaching')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'practitionersTeaching'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :template="(e) => gridBoolCellTemplate(e, 'practitionersTeaching')"
                                       :title="$t('registers.electronicLecturers.gridHeaders.practitionersTeaching')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'reasonForJoining'"
                                       :width="250"
                                       :lockable="true"
                                       :locked="false"
                                       :template="(e) => gridTextCellTemplate(e, 'reasonForJoining')"
                                       :title="$t('registers.electronicLecturers.gridHeaders.reasonForJoining')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'decisionForJoining'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :template="(e) => gridTextCellTemplate(e, 'decisionForJoining')"
                                       :title="$t('registers.electronicLecturers.gridHeaders.decisionForJoining')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'decisionForJoiningDate'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :format="'{0:dd.MM.yyyy HH:mm}'"
                                       :title="$t('registers.electronicLecturers.gridHeaders.decisionForJoiningDate')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'reasonForRelease'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :template="(e) => gridTextCellTemplate(e, 'reasonForRelease')"
                                       :title="$t('registers.electronicLecturers.gridHeaders.reasonForRelease')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'decisionForRelease'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :template="(e) => gridTextCellTemplate(e, 'decisionForRelease')"
                                       :title="$t('registers.electronicLecturers.gridHeaders.decisionForRelease')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'decisionForReleaseDate'"
                                       :width="150"
                                       :lockable="true"
                                       :locked="false"
                                       :format="'{0:dd.MM.yyyy HH:mm}'"
                                       :title="$t('registers.electronicLecturers.gridHeaders.decisionForReleaseDate')">
                    </kendo-grid-column>

                </kendo-grid>
            </div>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import JSZip from 'jszip'
    import PageTitle from '@/components/common/PageTitle';
    import { KendoGridPageSize } from '@/models/enums';
    import DatetimeCellDisplayTemplate from "@/components/grid/DatetimeCellDisplayTemplate";
    import personalizationService from '@/services/personalization.service';
    import GridUserSettings from "@/components/grid/UserSettings";
    import { mapGetters } from 'vuex';

    /*
    Renders the grid in "DejaVu Sans" font family, which is
    declared in kendo.common.css. It also declares the paths to the
    fonts below using <tt>kendo.pdf.defineFont</tt>, because the
    stylesheet is hosted on a different domain.
    */
    kendo.pdf.defineFont({
        "DejaVu Sans": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans.ttf",
        "DejaVu Sans|Bold": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Bold.ttf",
        "DejaVu Sans|Bold|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "DejaVu Sans|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "WebComponentsIcons": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/glyphs/WebComponentsIcons.ttf"
    });


    export default {
        props: {},
        components: {
            PageTitle,
            DatetimeCellDisplayTemplate,
            'grid-user-settings': GridUserSettings
        },
        data() {
            return {
                componentName: "LectorsElRegisterGrid",
                titleKey: 'registers.lecturers.title',
                defaultGridRows: this.$store.getters.defaultGridRows,
                settingsTooltipText: this.$t('buttons.settings.tooltipText'),
                customSettingsLoaded: false,
                searchQuery: '',
                schemaModelFields: {
                    id: {
                        name: 'id',
                        type: 'number',
                        editable: false,
                        nullable: false,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.id'
                    },
                    fullName: {
                        name: 'fullName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.fullName'
                    },
                    titleDegree: {
                        name: 'titleDegree',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.titleDegree'
                    },
                    firstName: {
                        name: 'firstName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.firstName'
                    },
                    middleName: {
                        name: 'middleName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.middleName'
                    },
                    lastName: {
                        name: 'lastName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.lastName'
                    },
                    positionStatistic: {
                        name: 'positionStatistic',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.positionStatistic'
                    },
                    position: {
                        name: 'position',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.position'
                    },
                    institutionCompanyName: {
                        name: 'institutionCompanyName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.institutionCompanyName'
                    },
                    institutionCompanyCountry: {
                        name: 'institutionCompanyCountry',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.institutionCompanyCountry'
                    },
                    institutionCompanyCity: {
                        name: 'institutionCompanyCity',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.institutionCompanyCity'
                    },
                    thematicAreas: {
                        name: 'thematicAreas',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.thematicAreas'
                    },
                    themes: {
                        name: 'themes',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.themes'
                    },
                    lectorsTeaching: {
                        name: 'lectorsTeaching',
                        type: 'boolean',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.lectorsTeaching'
                    },
                    universityTeaching: {
                        name: 'universityTeaching',
                        type: 'boolean',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.universityTeaching'
                    },
                    nipTeaching: {
                        name: 'nipTeaching',
                        type: 'boolean',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.nipTeaching'
                    },
                    practitionersTeaching: {
                        name: 'practitionersTeaching',
                        type: 'boolean',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.practitionersTeaching'
                    },
                    reasonForJoining: {
                        name: 'reasonForJoining',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.reasonForJoining'
                    },
                    decisionForJoining: {
                        name: 'decisionForJoining',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.decisionForJoining'
                    },
                    decisionForJoiningDate: {
                        name: 'decisionForJoiningDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.decisionForJoiningDate'
                    },
                    reasonForRelease: {
                        name: 'reasonForRelease',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.reasonForRelease'
                    },
                    decisionForRelease: {
                        name: 'decisionForJoining',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.decisionForRelease'
                    },
                    decisionForReleaseDate: {
                        name: 'decisionForJoiningDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.electronicLecturers.gridHeaders.decisionForReleaseDate'
                    },
                },
                rawHtml: "",
                transportSettings: {
                    read: {
                        url: `${this.$store.getters.baseUrl}/api/electronicLecturers/list`,
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                skipGridShowEllipsis: false,
                gridOptions: {
                    ...this.$store.getters.gridOptions,
                    editable: {
                        mode: 'popup', /* inline, popup */
                        window: {
                            title: this.$t('person.gridTitle'),
                            animation: true
                        },
                        move: true
                    },
                },
                showUserSettingsForm: false,
            }
        },
        methods: {
            dataBound(e) {
                // This is needed for grid state save

            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) }
                } else {
                    return kendo.stringify(options);
                }
            },
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            datetimeDisplayTemplate(date, useShortFormat) {
                return {
                    template: Vue.component(DatetimeCellDisplayTemplate.name, DatetimeCellDisplayTemplate),
                    templateArgs: {
                        dateValue: date,
                        showShortDate: useShortFormat
                    },
                };
            },
            datetimeDisplayTemplateJoining(e) {
                return this.datetimeDisplayTemplate(e.decisionForJoiningDate, false);
            },
            datetimeDisplayTemplateRelease(e) {
                return this.datetimeDisplayTemplate(e.decisionForReleaseDate, false);
            },
            gridBoolCellTemplate(dataItem, fieldName) {
                if (!dataItem || !dataItem.hasOwnProperty(fieldName)) {
                    return '';
                }

                return '<span class="badge ' + (dataItem[fieldName] ? 'badge-success' : 'badge-secondary') + '">' + kendo.htmlEncode(dataItem[fieldName] ? this.$t('yes') : this.$t('no')) + '</span>';
            },
            gridTextCellTemplate(dataItem, fieldName) {
                if (dataItem && fieldName) {
                    return this.$helpers.gridTextCellTemplate(dataItem, fieldName, this.userSettings.showColumnValueFullText);
                }

                return '';
            },
            excelExport() {
                this.$http.get(`api/electronicLecturers/export`, { responseType: 'blob' })
                    .then(response => {
                        ;
                        const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'LecturersERegister.xlsx'); // or any other extension
                        document.body.appendChild(link);
                        link.click();
                    }).catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            // This is needed for grid state save and mass search
            getGridOptions() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    return grid.getOptions();
                }
            },
            saveGridSettings() {
                const gridSettings = this.getGridOptions();
                const grid = this.$refs.grid.kendoWidget();
                if (gridSettings && grid) {
                    this.$refs.userSettings.setGridSettings(gridSettings);

                    personalizationService
                        .saveGridSettings(this.componentName, gridSettings)
                        .then(response => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                                text: this.$helpers.responseMessage(response),
                                color: 'success',
                                time: this.$store.getters.notificationDuration
                            });
                        })
                        .catch(error => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                text: this.$helpers.errorText(error),
                                color: this.$helpers.errorColor(error),
                                time: this.$store.getters.notificationDuration
                            })
                        });
                }
            },
            loadGridSettings() {
                personalizationService
                    .loadGridSettings(this.componentName)
                    .then(response => {
                        try {
                            var options = personalizationService
                                .mergePersonGridSettings(this.getGridOptions(), response.data.settings);

                            let grid = this.$refs.grid.kendoWidget();
                            if (options && grid) {
                                grid.setOptions(options);

                                if (options.pageable.size !== grid.dataSource.options.pageSize) {
                                    grid.dataSource.pageSize(options.pageable.size);
                                    grid.refresh();
                                }

                                const userSettingsEditor = this.$refs.userSettings;
                                if (userSettingsEditor) {
                                    userSettingsEditor.setGridSettings(grid.getOptions());
                                }
                            }

                            this.customSettingsLoaded = true;
                        } catch (e) {
                            this.customSettingsLoaded = true;
                        }
                    })
                    .catch(error => {
                        this.customSettingsLoaded = true;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            deleteGridSettings() {
                personalizationService
                    .delteGridSettings(this.componentName)
                    .then(response => {
                        this.$router.go();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            userSettingsChanged(userSettings, pageSizeChanged) {
                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.setOptions(userSettings);
                    if (pageSizeChanged === true) {
                        if (userSettings.pageable.size === KendoGridPageSize.All) {
                            grid.dataSource.pageSize(this.$refs["datasource1"].kendoDataSource.total());
                        } else {
                            grid.dataSource.pageSize(userSettings.pageable.size);
                        }
                        grid.refresh();
                    }
                }
            },
            changeGridSettings() {
                this.showUserSettingsForm = !this.showUserSettingsForm;
            },
            clearFilters() {
                personalizationService.claerState(this.componentName);

                this.searchQuery = '';
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);
                    grid.dataSource.filter({});
                }
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    })

                    return;
                }

                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
            // End
        },
        mounted: function () {

        },
        created: function () {
            window.JSZip = JSZip // Необходимо е за да работи експорта в excel
        },
        computed: {
            ...mapGetters(['claims', 'isEnVersion']),
            token: function () {
                return this.$store.getters.authToken;
            },
            userSettings() {
                const grid = this.$refs.grid;
                if (grid) {
                    return grid.kendoWidget().getOptions();
                }

                return null;
            },
            hasReadRights() {
                return this.$store.getters.claims.lecturersElRegister.read;
            },
        },
        watch: {
            hasReadRights: {
                immediate: true,
                handler(newVal) {
                    if (newVal === true) {
                        this.loadGridSettings();
                    }
                }
            }
        }
    }
</script>

<style scoped>
    /*
        Use the DejaVu Sans font for display and embedding in the PDF file.
        The standard PDF fonts have no support for Unicode characters.
    */
    .k-pdf-export .k-grid {
        font-family: "DejaVu Sans", "Arial", sans-serif;
    }

    .k-pdf-export .k-filterable .k-grid-filter {
        display: none;
    }

    .k-pdf-export .k-with-icon .k-header-column-menu {
        display: none;
    }

    .k-grid .k-grid-header .k-header .k-link {
        height: auto;
    }

    .k-grid .k-grid-header .k-header {
        white-space: normal;
    }

    .k-grid-edit, .k-grid-details, .k-grid-delete, .k-grid-pdf, .k-grid-excel {
        padding: 0;
        margin-bottom: 2px;
    }

    .k-grid td {
        white-space: nowrap;
        text-overflow: ellipsis;
    }
</style>
